import React, { useEffect, useMemo, useRef, useState } from "react";

import { motion } from "framer-motion";
import { cn } from "lib/utils";
import {
  Building2,
  Check,
  Clock,
  ClipboardCopy,
  Copy,
  Loader2,
  Share2,
} from "lucide-react";
import { toast } from "utils/toast";

import { fadeInUp } from "js/components/Quotas/animations";
import { Button } from "js/components/ui/button";

import { PaymentData } from "js/pages/Payments/types";

interface SpeiPaymentSlipProps {
  paymentData: PaymentData;
  amount: number;
  onCancel?: () => void;
  cancelLoading?: boolean;
}

interface CountdownState {
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

function useCountdown(dueDateStr?: string) {
  const [remaining, setRemaining] = useState<CountdownState | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!dueDateStr) return;
    const dueDate = new Date(dueDateStr);

    const update = () => {
      const diff = dueDate.getTime() - Date.now();
      if (diff <= 0) {
        setRemaining({ hours: 0, minutes: 0, seconds: 0, totalMs: 0 });
        if (intervalRef.current) clearInterval(intervalRef.current);
        return;
      }
      const hours = Math.floor(diff / 3_600_000);
      const minutes = Math.floor((diff % 3_600_000) / 60_000);
      const seconds = Math.floor((diff % 60_000) / 1_000);
      setRemaining({ hours, minutes, seconds, totalMs: diff });
    };

    update();
    intervalRef.current = setInterval(update, 1_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [dueDateStr]);

  return remaining;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
  }).format(amount);

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatShortDeadline = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("es-MX", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
};

function formatClabe(clabe?: string): string {
  if (!clabe) return "";
  return clabe.replace(/(\d{4})/g, "$1 ").trim();
}

function buildWhatsAppMessage(
  data: PaymentData,
  amount: number,
  bank: "bbva" | "other",
  beneficiary: string,
): string {
  if (bank === "bbva") {
    const lines = [
      "\u{1F4CB} *Pago BBVA \u2013 Servicio CIE*",
      `\u{1F4B0} Monto: ${formatCurrency(amount)} MXN`,
      data.agreement ? `\u{1F522} Convenio CIE: ${data.agreement}` : "",
      data.name ? `\u{1F4DD} Referencia: ${data.name}` : "",
      data.due_date
        ? `\u23F0 Fecha l\u00EDmite: ${formatDate(data.due_date)}`
        : "",
    ].filter(Boolean);
    return lines.join("\n");
  }

  const lines = [
    "\u{1F4CB} *Instrucciones de pago SPEI*",
    `\u{1F4B0} Monto: ${formatCurrency(amount)} MXN`,
    beneficiary ? `\u{1F3E2} Beneficiario: ${beneficiary}` : "",
    "\u{1F3E6} Banco destino: BBVA Bancomer",
    data.clabe ? `CLABE: ${formatClabe(data.clabe)}` : "",
    data.name ? `\u{1F4DD} Concepto: ${data.name}` : "",
    data.agreement ? `\u{1F522} Referencia: ${data.agreement}` : "",
    data.due_date
      ? `\u23F0 Fecha l\u00EDmite: ${formatDate(data.due_date)}`
      : "",
  ].filter(Boolean);
  return lines.join("\n");
}

function buildPlainTextDetails(
  data: PaymentData,
  amount: number,
  bank: "bbva" | "other",
  beneficiary: string,
): string {
  if (bank === "bbva") {
    const lines = [
      "Pago BBVA - Servicio CIE",
      `Monto: ${formatCurrency(amount)} MXN`,
      data.agreement ? `Convenio CIE: ${data.agreement}` : "",
      data.name ? `Referencia: ${data.name}` : "",
      data.due_date ? `Fecha limite: ${formatDate(data.due_date)}` : "",
    ].filter(Boolean);
    return lines.join("\n");
  }

  const lines = [
    "Instrucciones de pago SPEI",
    `Monto: ${formatCurrency(amount)} MXN`,
    beneficiary ? `Beneficiario: ${beneficiary}` : "",
    "Banco destino: BBVA Bancomer",
    data.clabe ? `CLABE: ${formatClabe(data.clabe)}` : "",
    data.name ? `Concepto: ${data.name}` : "",
    data.agreement ? `Referencia: ${data.agreement}` : "",
    data.due_date ? `Fecha limite: ${formatDate(data.due_date)}` : "",
  ].filter(Boolean);
  return lines.join("\n");
}

/* ------------------------------------------------------------------ */
/*  CopyField — full row is tappable                                   */
/* ------------------------------------------------------------------ */

const CopyField: React.FC<{ label: string; value: string; mono?: boolean; stripSpaces?: boolean }> = ({
  label,
  value,
  mono = true,
  stripSpaces = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(stripSpaces ? value.replace(/\s/g, "") : value);
      setCopied(true);
      toast.success(`${label} copiado`);
      setTimeout(() => setCopied(false), 2_000);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        "group flex w-full items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-all",
        copied
          ? "border-emerald-200 bg-emerald-50"
          : "border-border/60 bg-muted/30 hover:border-border hover:bg-muted/50 active:bg-muted/70",
      )}
    >
      <div className="min-w-0 flex-1">
        <span className="block text-[11px] font-medium text-muted-foreground">
          {label}
        </span>
        <span
          className={cn(
            "block truncate text-sm font-semibold text-foreground mt-0.5",
            mono && "font-mono tracking-wide",
          )}
        >
          {value}
        </span>
      </div>
      <span
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-md transition-colors",
          copied
            ? "bg-emerald-100 text-emerald-600"
            : "text-muted-foreground/50 group-hover:bg-muted group-hover:text-foreground",
        )}
      >
        {copied ? (
          <Check className="size-4" />
        ) : (
          <Copy className="size-3.5" />
        )}
      </span>
    </button>
  );
};

/* ------------------------------------------------------------------ */
/*  DataField — non-copyable display                                   */
/* ------------------------------------------------------------------ */

const DataField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="px-3.5 py-1">
    <span className="block text-[11px] font-medium text-muted-foreground">
      {label}
    </span>
    <span className="block text-sm font-bold text-foreground mt-0.5">
      {value}
    </span>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Deadline indicator                                                 */
/* ------------------------------------------------------------------ */

const DeadlineIndicator: React.FC<{
  countdown: CountdownState;
  dueDateStr?: string;
  timerColor: string;
}> = ({ countdown, dueDateStr, timerColor }) => {
  const isLive = countdown.totalMs <= 2 * 3_600_000;

  if (countdown.totalMs <= 0) {
    return (
      <div className="flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-red-600">
        <Clock className="size-3" />
        <span className="text-xs font-medium">
          Vencido{dueDateStr ? ` · ${formatShortDeadline(dueDateStr)}` : ""}
        </span>
      </div>
    );
  }

  if (isLive) {
    return (
      <div
        className={cn(
          "flex items-center gap-1.5 rounded-full border px-3 py-1",
          countdown.totalMs <= 3_600_000
            ? "border-red-200 bg-red-50 text-red-600"
            : "border-amber-200 bg-amber-50 text-amber-600",
        )}
      >
        <Clock className="size-3.5" />
        <span className="font-mono text-xs font-bold">
          {String(countdown.hours).padStart(2, "0")}:
          {String(countdown.minutes).padStart(2, "0")}:
          {String(countdown.seconds).padStart(2, "0")}
        </span>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs", timerColor, "border-border/60 bg-white/60")}>
      <Clock className="size-3" />
      <span>Vence: {dueDateStr ? formatShortDeadline(dueDateStr) : ""}</span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const SpeiPaymentSlip: React.FC<SpeiPaymentSlipProps> = ({
  paymentData,
  amount,
  onCancel,
  cancelLoading,
}) => {
  const beneficiary = paymentData.merchant_name || paymentData.name || "";
  const countdown = useCountdown(paymentData.due_date);
  const [activeBank, setActiveBank] = useState<"bbva" | "other">("other");
  const [allCopied, setAllCopied] = useState(false);

  const timerColor = useMemo(() => {
    if (!countdown) return "text-muted-foreground";
    if (countdown.totalMs > 24 * 3_600_000) return "text-emerald-600";
    if (countdown.totalMs > 2 * 3_600_000) return "text-amber-600";
    return "text-red-600";
  }, [countdown]);

  const handleCopyAll = async () => {
    try {
      const text = buildPlainTextDetails(
        paymentData,
        amount,
        activeBank,
        beneficiary,
      );
      await navigator.clipboard.writeText(text);
      setAllCopied(true);
      toast.success("Todos los datos copiados");
      setTimeout(() => setAllCopied(false), 2_500);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <motion.div variants={fadeInUp} initial="hidden" animate="visible">
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
        {/* ── Header: amount + deadline ───────────────────────── */}
        <div className="border-b border-indigo-200/60 bg-gradient-to-br from-indigo-50/80 to-blue-50/60 px-5 py-4 dark:border-indigo-900 dark:from-indigo-950/30 dark:to-blue-950/20">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">
                Transfiere exactamente
              </p>
              <p className="mt-1.5 text-3xl font-bold tracking-tight text-indigo-950 dark:text-indigo-100">
                {formatCurrency(amount)}
              </p>
              <p className="mt-1 text-[11px] text-indigo-400 dark:text-indigo-500">
                El monto debe ser exacto para que se acredite correctamente
              </p>
            </div>
            {countdown && (
              <DeadlineIndicator
                countdown={countdown}
                dueDateStr={paymentData.due_date}
                timerColor={timerColor}
              />
            )}
          </div>
        </div>

        {/* ── Bank tabs ──────────────────────────────────────── */}
        <div className="flex border-b border-border/60">
          <button
            type="button"
            onClick={() => setActiveBank("bbva")}
            className={cn(
              "flex-1 py-2.5 text-center text-xs font-medium transition-colors",
              activeBank === "bbva"
                ? "border-b-2 border-foreground text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Building2 className="mb-0.5 mr-1 inline-block size-3.5" />
            BBVA
          </button>
          <button
            type="button"
            onClick={() => setActiveBank("other")}
            className={cn(
              "flex-1 py-2.5 text-center text-xs font-medium transition-colors",
              activeBank === "other"
                ? "border-b-2 border-foreground text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            Otro banco
          </button>
        </div>

        {/* ── Bank-specific fields ───────────────────────────── */}
        <div className="flex flex-col gap-2.5 p-4">
          {activeBank === "bbva" ? (
            <>
              <p className="text-xs text-muted-foreground">
                En el men&uacute;{" "}
                <span className="font-medium text-foreground">
                  &quot;Pagar&quot;
                </span>{" "}
                selecciona{" "}
                <span className="font-medium text-foreground">
                  &quot;De Servicios&quot;
                </span>{" "}
                e ingresa:
              </p>
              {paymentData.agreement && (
                <CopyField
                  label="N&uacute;mero de convenio CIE"
                  value={paymentData.agreement}
                  stripSpaces
                />
              )}
              {paymentData.name && (
                <CopyField label="Referencia" value={paymentData.name} />
              )}
              <DataField
                label="Importe"
                value={`${formatCurrency(amount)} MXN`}
              />
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Realiza una transferencia SPEI con estos datos:
              </p>
              <CopyField
                label="Beneficiario"
                value={beneficiary}
                mono={false}
              />
              <CopyField
                label="Banco destino"
                value="BBVA Bancomer"
                mono={false}
              />
              {paymentData.clabe && (
                <CopyField
                  label="CLABE"
                  value={formatClabe(paymentData.clabe)}
                  stripSpaces
                />
              )}
              {paymentData.name && (
                <CopyField label="Concepto / motivo / descripción" value={paymentData.name} />
              )}
              {paymentData.agreement && (
                <CopyField label="Referencia numérica" value={paymentData.agreement} stripSpaces />
              )}
              <DataField
                label="Importe"
                value={`${formatCurrency(amount)} MXN`}
              />
            </>
          )}
        </div>

        {/* ── Action buttons ─────────────────────────────────── */}
        <div className="flex flex-col gap-2.5 border-t border-border/60 bg-muted/20 p-4">
          <Button
            type="button"
            variant="outline"
            className={cn(
              "w-full gap-2 transition-all",
              allCopied &&
                "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-700",
            )}
            onClick={handleCopyAll}
          >
            {allCopied ? (
              <>
                <Check className="size-4" />
                Datos copiados
              </>
            ) : (
              <>
                <ClipboardCopy className="size-4" />
                Copiar todos los datos
              </>
            )}
          </Button>

          <a
            href={`https://wa.me/?text=${encodeURIComponent(buildWhatsAppMessage(paymentData, amount, activeBank, beneficiary))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 rounded-lg !border !border-green-200 !bg-green-50 py-2.5 text-sm font-medium !text-green-700 !no-underline transition-colors hover:!bg-green-100 dark:!border-green-800 dark:!bg-green-950 dark:!text-green-400 dark:hover:!bg-green-900"
          >
            <Share2 className="size-4" />
            Compartir por WhatsApp
          </a>

          {onCancel && (
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={onCancel}
              disabled={cancelLoading}
            >
              {cancelLoading && <Loader2 className="size-4 animate-spin" />}
              Cambiar método de pago
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default SpeiPaymentSlip;
